import { zodResolver } from '@hookform/resolvers/zod'
import { PlusIcon } from '@phosphor-icons/react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useAddSocialLink } from '@/api/communication/social-links/add-social-link'
import { SelectFormField } from '@/components/form/select-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { toastFactory } from '@/factories'
import { SocialLinkService } from '@/models'

import { getServiceOptions, makeSocialLinkFormSchema } from '../social-links.constants'
import { SocialLinkFormData } from '../social-links.types'
import * as S from './add-social-link-popup.styled'

interface AddSocialLinkPopupProps {
  onClose: VoidFunction
  buildingId: number
  existingServices: SocialLinkService[]
}

export const AddSocialLinkPopup = ({
  onClose,
  buildingId,
  existingServices,
}: AddSocialLinkPopupProps) => {
  const { t } = useTranslation(['communication'])
  const { mutateAsync: addSocialLink, isPending } = useAddSocialLink()

  const availableOptions = getServiceOptions(t).filter(
    (option) => !existingServices.includes(option.value),
  )

  const form = useForm<SocialLinkFormData>({
    defaultValues: {
      link: '',
      service: availableOptions[0]?.value,
    },
    resolver: zodResolver(makeSocialLinkFormSchema(t)),
  })

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await addSocialLink({
        buildingId,
        link: data.link.trim(),
        service: data.service,
      })
      toastFactory.success(t('communication:socialLinks.addPopup.submitSuccess'))
      onClose()
    } catch {
      toastFactory.error(t('communication:socialLinks.addPopup.submitError'))
    }
  })

  return (
    <Modal
      title={t('communication:socialLinks.addPopup.title')}
      headerIcon={PlusIcon}
      size="small"
      footer={
        <>
          <Button use="secondary" disabled={isPending} onClick={onClose}>
            {t('communication:socialLinks.addPopup.back')}
          </Button>
          <Button loading={isPending} onClick={onSubmit}>
            {t('communication:socialLinks.addPopup.submit')}
            <Icon icon={PlusIcon} size="1.8rem" />
          </Button>
        </>
      }
      onClose={onClose}
    >
      <FormProvider {...form}>
        <S.Fields>
          <SelectFormField
            name="service"
            label={t('communication:socialLinks.form.service.label')}
            placeholder={t('communication:socialLinks.form.service.placeholder')}
            options={availableOptions}
            disabled={isPending}
          />
          <TextInputFormField
            name="link"
            label={t('communication:socialLinks.form.link.label')}
            placeholder={t('communication:socialLinks.form.link.placeholder')}
            disabled={isPending}
          />
        </S.Fields>
      </FormProvider>
    </Modal>
  )
}
